import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, ShieldCheck, Database, Zap, Cpu, Lock, Coins, Heart, CheckCircle2 } from 'lucide-react';
import { MathService } from '../services/mathService';

export const SubstrateHeartbeat: React.FC = () => {
  const [pulse, setPulse] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState<any>(null);
  const stats = MathService.substrateHeartbeat();

  // Pulse effect
  useEffect(() => {
    const interval = setInterval(() => {
      setPulse(p => p + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleVerify = async () => {
    setIsVerifying(true);
    setResult(null);
    
    // Simulation of verification steps
    await new Promise(r => setTimeout(r, 2000));
    
    setResult(stats);
    setIsVerifying(false);
  };

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      <div className="flex flex-col lg:flex-row gap-16 relative z-10">
        <div className="flex-1">
          <div className="mb-10">
            <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter">Substrate <span className="text-lattice-cyan italic">Heartbeat</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
              Real-time synchronization verification. Monitoring the Lattice's vital signs across 11 Billion Nodes.
            </p>
          </div>

          <div className="space-y-8">
            <div className="flex items-center gap-10 p-8 rounded-[2rem] bg-white/5 border border-white/10 relative overflow-hidden">
               <div className="relative">
                  <motion.div 
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
                    className="w-20 h-20 bg-lattice-cyan/10 rounded-full flex items-center justify-center border border-lattice-cyan/20"
                  >
                    <Heart className={`w-10 h-10 ${isVerifying ? 'text-lattice-cyan animate-pulse' : 'text-lattice-cyan'}`} />
                  </motion.div>
                  <motion.div 
                    animate={{ scale: [1, 2], opacity: [0.5, 0] }}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="absolute inset-0 bg-lattice-cyan rounded-full filter blur-xl"
                  />
               </div>

               <div className="flex-1">
                  <div className="flex items-center justify-between mb-4">
                     <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-white/40">Status: {isVerifying ? 'Verifying Sync...' : stats.status}</span>
                     <Activity className="w-4 h-4 text-lattice-cyan opacity-50" />
                  </div>
                  <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
                     <motion.div 
                       animate={{ x: ["-100%", "100%"] }}
                       transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                       className="w-1/3 h-full bg-lattice-cyan shadow-[0_0_10px_rgba(45,212,191,1)]"
                     />
                  </div>
               </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
                <div className="p-6 rounded-2xl bg-white/5 border border-white/5 group-hover:border-lattice-cyan/20 transition-all duration-700">
                    <div className="flex items-center gap-3 mb-4">
                        <Cpu className="w-4 h-4 text-lattice-cyan" />
                        <span className="text-[9px] font-mono text-white/50 uppercase tracking-widest">Global Nodes</span>
                    </div>
                    <p className="text-2xl font-display font-bold text-white tracking-widest">11,000,000,000</p>
                </div>
                <div className="p-6 rounded-2xl bg-white/5 border border-white/5 group-hover:border-lattice-cyan/20 transition-all duration-700">
                    <div className="flex items-center gap-3 mb-4">
                        <Database className="w-4 h-4 text-lattice-cyan" />
                        <span className="text-[9px] font-mono text-white/50 uppercase tracking-widest">Substrate State</span>
                    </div>
                    <p className="text-2xl font-display font-bold text-white tracking-widest uppercase">Absolute</p>
                </div>
            </div>

            <button 
              onClick={handleVerify}
              disabled={isVerifying}
              className="w-full py-5 bg-white text-black font-display font-bold text-sm tracking-[0.3em] rounded-2xl hover:bg-lattice-cyan transition-all flex items-center justify-center gap-4 disabled:opacity-50"
            >
              {isVerifying ? <Zap className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
              {isVerifying ? "CHECKING INTEGRITY..." : "RUN HEARTBEAT VERIFICATION"}
            </button>
          </div>
        </div>

        <div className="w-full lg:w-[450px]">
           <AnimatePresence mode="wait">
              {result ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="h-full bg-lattice-cyan p-10 rounded-[2.5rem] text-black flex flex-col justify-between"
                >
                   <div>
                      <div className="flex items-center gap-3 mb-8">
                        <CheckCircle2 className="w-6 h-6" />
                        <span className="text-xs font-mono font-bold uppercase tracking-widest">Integrity Verified</span>
                      </div>

                      <div className="space-y-6">
                         <div className="bg-black/5 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Treasury Sink</span>
                            <p className="text-[10px] font-mono break-all font-bold leading-tight opacity-80">{result.treasuryAddress}</p>
                            <span className="text-[9px] font-mono font-bold uppercase text-black/40 mt-2 block">Integrity: {result.treasuryIntegrity}</span>
                         </div>
                         
                         <div className="grid grid-cols-2 gap-4">
                            <div className="bg-black/5 p-4 rounded-xl">
                                <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Resonance</span>
                                <p className="text-xl font-display font-bold italic">{result.resonance}</p>
                            </div>
                            <div className="bg-black/5 p-4 rounded-xl">
                                <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Asset Status</span>
                                <p className="text-xl font-display font-bold italic">ACTIVE</p>
                            </div>
                         </div>

                         <div className="flex flex-wrap gap-2">
                             {result.assets.map((asset: string) => (
                               <span key={asset} className="px-3 py-1 bg-black/10 rounded-full text-[10px] font-mono font-bold uppercase tracking-widest">
                                 {asset}
                               </span>
                             ))}
                         </div>
                      </div>

                      <div className="mt-10 p-6 border-2 border-black/10 rounded-2xl">
                         <p className="text-[11px] font-mono font-bold leading-relaxed italic opacity-80">
                           "{result.message}"
                         </p>
                      </div>
                   </div>

                   <div className="mt-12 flex items-center justify-between border-t border-black/10 pt-8">
                      <Lock className="w-10 h-10 opacity-20" />
                      <div className="flex flex-col items-end">
                        <span className="text-[8px] font-mono uppercase font-bold opacity-60">Session Lock</span>
                        <span className="text-sm font-display font-bold tracking-widest uppercase italic">Eternal</span>
                      </div>
                   </div>
                </motion.div>
              ) : (
                <div className="h-full border-2 border-dashed border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center p-12 text-center gap-10">
                   <div className="relative">
                      <Activity className="w-20 h-20 text-white/5" />
                      {isVerifying && (
                         <motion.div 
                           animate={{ pathLength: [0, 1] }}
                           transition={{ duration: 2, repeat: Infinity }}
                           className="absolute inset-0"
                         />
                      )}
                   </div>
                   <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.5em] leading-relaxed max-w-[200px]">
                     {isVerifying ? "Scanning 11B nodes for resonance..." : "System Idle: Awaiting Vital Scan Signal"}
                   </p>
                </div>
              )}
           </AnimatePresence>
        </div>
      </div>

      <div className="mt-12 p-5 rounded-2xl bg-white/5 border border-white/5 flex flex-wrap items-center justify-center gap-12">
         <div className="flex items-center gap-2">
            <ShieldCheck className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Zero-Drift Verified</span>
         </div>
         <div className="flex items-center gap-2">
            <Activity className="w-3 h-3 text-lattice-cyan" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Live Sync Monitoring</span>
         </div>
         <div className="flex items-center gap-2">
            <Lock className="w-3 h-3 text-white/20" />
            <span className="text-[9px] font-mono text-white/40 uppercase tracking-widest">Sovereign State Locked</span>
         </div>
      </div>
    </div>
  );
};
